import {
    Box,
    Button,
    Chip,
    FormControl,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Eye,
    Pencil,
    Search,
    Trash2,
    UserCog,
    X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileCrudList from '../../component/common/MobileCrud';
import { KitField, KitSelect } from '../../component/common/MobileKitExtras';
import { protoIcon } from '../../component/common/ProtoIcon';
import UserViewDialog from './View';

const UsersIcon = protoIcon('users');
const EyeIcon = protoIcon('eye');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');

const AVATAR_TONES = ['soft', 'purple', 'green', 'amber'];
// Tom do avatar pelo nome (estável entre renders): alterna como no protótipo.
function userTone(name) {
    let hash = 0;
    String(name || '').split('').forEach((ch) => { hash = (hash * 31 + ch.charCodeAt(0)) % 997; });
    return AVATAR_TONES[hash % AVATAR_TONES.length];
}

// Pill de perfil — vermelho para "Administrador", roxo para os demais, mesma
// regra de cor do `Chip color="error"`/`"secondary"` do `users/List.js`
// anterior (prototipo-lista-usuarios.html). Reaproveitado por `View.js`.
export function ProfilePill({ label }) {
    const isAdmin = label === 'Administrador';
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                px: '10px',
                py: '5px',
                borderRadius: '20px',
                bgcolor: isAdmin ? 'error.bg' : 'secondary.bg',
                color: isAdmin ? 'error.main' : 'secondary.main',
            }}
        >
            {label || '—'}
        </Box>
    );
}

// Iniciais do avatar — nome + sobrenome quando ambos disponíveis (mesma
// convenção de `suppliers/Form.js getInitials()`), primeiras 2 letras do
// nome quando só o nome está disponível.
function getUserInitials(row) {
    const first = (row?.name || '').trim();
    const last = (row?.last_name || '').trim();
    if (first && last) return (first[0] + last[0]).toUpperCase();
    if (first) return first.slice(0, 2).toUpperCase();
    return '?';
}

// Filtro aplicado só ao clicar "Buscar"/Enter — mesma técnica de
// `suppliers/List.js` (report-toolbar §6.1): o estado do campo (`profileFilter`/
// `search`) é distinto do filtro efetivamente aplicado (`appliedProfile`/
// `appliedSearch`), que alimenta os chips removíveis. Mesmas condições de
// filtro do `users/List.js` anterior (`filterData`): perfil por `profile_id`,
// busca por nome/sobrenome/usuário.
function applyUserFilters(list, profileId, searchValue) {
    let result = Array.isArray(list) ? [...list] : [];

    if (profileId) {
        result = result.filter((item) => item && item.profile_id === profileId);
    }

    if (searchValue && searchValue.trim()) {
        const term = searchValue.trim().toLowerCase();
        result = result.filter((item) => {
            const name = (item?.name || '').toLowerCase();
            const lastName = (item?.last_name || '').toLowerCase();
            const username = (item?.username || '').toLowerCase();
            return name.includes(term) || lastName.includes(term) || username.includes(term);
        });
    }

    return result;
}

export default function List() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();

    const [users, setUsers] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Valores dos campos do formulário de filtro (não aplicados ainda).
    const [profileFilter, setProfileFilter] = useState('');
    const [search, setSearch] = useState('');
    // Filtros efetivamente aplicados — alimentam os chips removíveis.
    const [appliedProfile, setAppliedProfile] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');

    const [viewUserId, setViewUserId] = useState(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const fetchProfiles = async () => {
        try {
            const response = await httpClient.get('/profiles');
            setProfiles(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching profiles:', error);
            setProfiles([]);
        }
    };

    const fetchData = async () => {
        try {
            const response = await httpClient.get('/users');
            const data = Array.isArray(response.data) ? response.data : [];
            setUsers(data);
            setFilteredData(applyUserFilters(data, appliedProfile, appliedSearch));
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
            setFilteredData([]);
        }
    };

    useEffect(() => {
        fetchProfiles();
        fetchData().finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApplyFilters = () => {
        setAppliedProfile(profileFilter);
        setAppliedSearch(search);
        setFilteredData(applyUserFilters(users, profileFilter, search));
    };

    const handleClearFilters = () => {
        setProfileFilter('');
        setSearch('');
        setAppliedProfile('');
        setAppliedSearch('');
        setFilteredData(users);
    };

    const handleRemoveProfileFilter = () => {
        setProfileFilter('');
        setAppliedProfile('');
        setFilteredData(applyUserFilters(users, '', appliedSearch));
    };

    const handleRemoveSearchFilter = () => {
        setSearch('');
        setAppliedSearch('');
        setFilteredData(applyUserFilters(users, appliedProfile, ''));
    };

    const handleViewUser = (row) => {
        setViewUserId(row.id);
        setOpenViewDialog(true);
    };

    const handleDeleteUser = (row) => {
        setUserToDelete(row);
        setOpenDialogConfirmDelete(true);
    };

    const confirmDelete = async () => {
        try {
            setDeleting(true);
            await httpClient.delete(`/users/${userToDelete.id}`);
            setOpenDialogConfirmDelete(false);
            setUserToDelete(null);
            await fetchData();
            setSnackbarMessage('Usuário excluído com sucesso!');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Error deleting user:', error);
            setSnackbarMessage('Erro ao excluir usuário.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setDeleting(false);
        }
    };

    const columns = [
        {
            path: 'name',
            name: 'Nome',
            invisible: false,
            render: (row) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#fff',
                            backgroundImage: theme.palette.gradient.brand,
                        }}
                    >
                        {getUserInitials(row)}
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                            {row.name}
                        </Typography>
                        {row.last_name && (
                            <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                                {row.last_name}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ),
        },
        {
            path: 'username',
            name: 'Nome de usuário',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.username || '—'}
                </Typography>
            ),
        },
        {
            path: 'profile_name',
            name: 'Perfil',
            invisible: false,
            render: (row) => <ProfilePill label={row.profile_name} />,
        },
    ];

    const actions = [
        ...(hasPermission('user.view') ? [{
            icon: <Eye size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => handleViewUser(row),
                tooltip: 'Visualizar',
                style: { cursor: 'pointer' },
            },
        }] : []),
        ...(hasPermission('user.update') ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => navigate(`/users/edit/${row.id}`),
                tooltip: 'Editar',
                style: { cursor: 'pointer' },
            },
        }] : []),
        ...(hasPermission('user.delete') ? [{
            icon: <Trash2 size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (row) => handleDeleteUser(row),
                tooltip: 'Excluir',
                style: { cursor: 'pointer' },
            },
        }] : []),
    ];

    const selectedProfileName = profiles.find((p) => p.id === appliedProfile)?.name;

    const sharedDialogs = (
        <>
            <UserViewDialog
                open={openViewDialog}
                userId={viewUserId}
                profiles={profiles}
                onClose={() => setOpenViewDialog(false)}
            />

            <DialogForm
                iconType="warning"
                open={openDialogConfirmDelete}
                title="Atenção"
                confirmButtonText="Sim, Excluir"
                cancelButtonText="Cancelar"
                content={`Tem certeza que deseja excluir o usuário "${userToDelete?.name && userToDelete?.last_name
                    ? `${userToDelete.name} ${userToDelete.last_name}`
                    : userToDelete?.name || userToDelete?.username || 'este usuário'}"?`}
                secondaryContent="Esta ação não pode ser desfeita. O usuário será removido permanentemente do sistema."
                onSubmit={confirmDelete}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setUserToDelete(null);
                }}
                loading={deleting}
                loadingText="Removendo..."
            />
        </>
    );

    // Shell mobile: lista do protótipo `sistema/usuarios.html` (mesmos dados, filtros e ações).
    if (isMobileShell) {
        const fullName = (row) => [row.name, row.last_name].filter(Boolean).join(' ');
        const handleMobileSearch = (value) => {
            setSearch(value);
            setAppliedSearch(value);
            setFilteredData(applyUserFilters(users, appliedProfile, value));
        };
        const applyMobileProfile = () => {
            setAppliedProfile(profileFilter);
            setFilteredData(applyUserFilters(users, profileFilter, appliedSearch));
        };
        const clearMobileProfile = () => {
            setProfileFilter('');
            setAppliedProfile('');
            setFilteredData(applyUserFilters(users, '', appliedSearch));
        };
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget="Usuários"
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobileCrudList
                    items={filteredData}
                    getKey={(row) => row.id}
                    getRow={(row) => ({
                        avatar: { text: getUserInitials(row), tone: userTone(fullName(row)) },
                        title: fullName(row),
                        sub: [row.profile_name, row.username].filter(Boolean).join(' · '),
                    })}
                    loading={loading}
                    search={search}
                    onSearchChange={handleMobileSearch}
                    searchPlaceholder="Nome ou usuário…"
                    filter={{
                        active: Boolean(appliedProfile),
                        onOpen: () => setProfileFilter(appliedProfile),
                        onApply: applyMobileProfile,
                        onClear: clearMobileProfile,
                        children: (
                            <KitField label="Perfil">
                                <KitSelect value={profileFilter} onChange={(e) => setProfileFilter(e.target.value)} aria-label="Perfil">
                                    <option value="">Todos os perfis</option>
                                    {profiles.map((profile) => (
                                        <option key={profile.id} value={profile.id}>{profile.name}</option>
                                    ))}
                                </KitSelect>
                            </KitField>
                        ),
                    }}
                    emptyIcon={UsersIcon}
                    emptyTitle="Nenhum usuário encontrado"
                    emptyText="Ajuste a busca ou os filtros para ver outros usuários."
                    fabLabel={hasPermission('user.create') ? 'Novo' : undefined}
                    onNew={() => navigate('/users/new')}
                    rowTitle={fullName}
                    rowActions={(row) => [
                        ...(hasPermission('user.view') ? [{ label: 'Visualizar', icon: EyeIcon, onClick: () => handleViewUser(row) }] : []),
                        ...(hasPermission('user.update') ? [{ label: 'Editar', icon: EditIcon, onClick: () => navigate(`/users/edit/${row.id}`) }] : []),
                        ...(hasPermission('user.delete') ? [{ label: 'Excluir', icon: TrashIcon, destructive: true, onClick: () => handleDeleteUser(row) }] : []),
                    ]}
                />
                {sharedDialogs}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget="Usuários"
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone-badge + título + descrição + ação (Novo usuário). */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <UserCog size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Usuários
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Gerencie os usuários com acesso ao sistema e o perfil de permissão de cada um.
                            </Typography>
                        </Box>
                    </Box>

                    {hasPermission('user.create') && (
                        <NewItemButton label="Novo usuário" onClick={() => navigate('/users/new')} />
                    )}
                </Box>

                {/* report-toolbar — Perfil + Buscar, em card separado. */}
                <FilterToolbar
                    plain
                    activeCount={profileFilter ? 1 : 0}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 300px' }}>
                            <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                                Buscar
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nome, sobrenome ou usuário…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={handleClearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleApplyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box data-wide>
                            <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                                Perfil
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 190 }}>
                                <Select
                                    value={profileFilter}
                                    displayEmpty
                                    onChange={(e) => setProfileFilter(e.target.value)}
                                    renderValue={(value) => (value ? profiles.find((p) => p.id === value)?.name : 'Todos os perfis')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todos os perfis</MenuItem>
                                    {profiles.map((profile) => (
                                        <MenuItem key={profile.id} value={profile.id}>{profile.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                </FilterToolbar>

                {/* chips-row — filtros ativos removíveis + contagem. */}
                <FilterChips>
                    {appliedProfile && (
                        <Chip
                            label={`Perfil: ${selectedProfileName || '—'}`}
                            size="small"
                            onDelete={handleRemoveProfileFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {appliedSearch && (
                        <Chip
                            label={`Busca: "${appliedSearch}"`}
                            size="small"
                            onDelete={handleRemoveSearchFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${filteredData.length} usuário${filteredData.length === 1 ? '' : 's'} encontrado${filteredData.length === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'success.bg', color: 'success.main' }}
                    />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={filteredData}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={hasPermission('user.view') ? handleViewUser : undefined}
                    rowPerPage={8}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="usuários"
                    sortableFields={['name', 'username']}
                    defaultSort={{ field: 'name', direction: 'asc' }}
                />
            </Box>

            <UserViewDialog
                open={openViewDialog}
                userId={viewUserId}
                profiles={profiles}
                onClose={() => setOpenViewDialog(false)}
            />

            <DialogForm
                iconType="warning"
                open={openDialogConfirmDelete}
                title="Atenção"
                confirmButtonText="Sim, Excluir"
                cancelButtonText="Cancelar"
                content={`Tem certeza que deseja excluir o usuário "${userToDelete?.name && userToDelete?.last_name
                    ? `${userToDelete.name} ${userToDelete.last_name}`
                    : userToDelete?.name || userToDelete?.username || 'este usuário'}"?`}
                secondaryContent="Esta ação não pode ser desfeita. O usuário será removido permanentemente do sistema."
                onSubmit={confirmDelete}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setUserToDelete(null);
                }}
                loading={deleting}
                loadingText="Removendo..."
            />
        </PageScaffold>
    );
}
